import { useEffect, useState } from "react";
import type { HTMLAttributes } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Grid,
  TextField,
  Stack,
  IconButton,
  Pagination,
  PaginationItem,
  Autocomplete,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Search } from "@mui/icons-material";
import { useGetProductsQuery } from "../../api/productsApi";
import {
  useGetGroupsComboQuery,
  useGetSegmentsComboQuery,
  useGetSubgroupsComboQuery,
} from "../../api/productComboApi";
import { useAppSelector } from "../../store/hooks";

import Spinner from "../../components/layout/Spinner";
import ProductCard from "../../components/cards/product/ProductCard";
import ProductDetailModal from "../../components/modals/product/ProductDetailModal";
import SearchFeedback from "../../components/feedback/SearchFeedback";
import ProductsListingTable from "../../components/tables/ProductsListingTable";
import Colors from "../../constants/Colors";
import { ProductComboItem } from "../../models/product/productCombo.model";

const productComboFilterOptions = createFilterOptions<ProductComboItem>({
  ignoreAccents: true,
  ignoreCase: true,
  matchFrom: "any",
  stringify: (option) => option.nome,
});

const renderProductComboOption = (
  props: HTMLAttributes<HTMLLIElement>,
  option: ProductComboItem,
) => (
  <li {...props} key={option.id}>
    {option.nome}
  </li>
);

const ProductsListPage = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNormalizingInitialUrl, setIsNormalizingInitialUrl] =
    useState<boolean>(() => location.search !== "?page=1");
  const query = new URLSearchParams(location.search);
  const page = parseInt(query.get("page") || "1");
  const filter = query.get("filtro");
  const segmento = query.get("segmento");
  const grupo = query.get("grupo");
  const subgrupo = query.get("subgrupo");
  let pageNumber = page;
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedSegments, setSelectedSegments] = useState<ProductComboItem[]>(
    [],
  );
  const [selectedGroups, setSelectedGroups] = useState<ProductComboItem[]>([]);
  const [selectedSubgroups, setSelectedSubgroups] = useState<
    ProductComboItem[]
  >([]);
  const [modalId, setModalId] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { data, isError, isFetching, isLoading, error } = useGetProductsQuery(
    {
      pageNumber,
      filter,
      segmento,
      grupo,
      subgrupo,
    },
    {
      skip: isNormalizingInitialUrl,
    },
  );
  const {
    data: segmentsData,
    isFetching: isSegmentsFetching,
    isLoading: isSegmentsLoading,
  } = useGetSegmentsComboQuery();
  const {
    data: groupsData,
    isFetching: isGroupsFetching,
    isLoading: isGroupsLoading,
  } = useGetGroupsComboQuery();
  const {
    data: subgroupsData,
    isFetching: isSubgroupsFetching,
    isLoading: isSubgroupsLoading,
  } = useGetSubgroupsComboQuery();
  const visualization = useAppSelector(
    (state) => state.visualization.visualization,
  );
  const segmentOptions =
    segmentsData?.segmento.filter((segment) => !segment.inativo) || [];
  const groupOptions =
    groupsData?.grupo.filter((group) => !group.inativo) || [];
  const subgroupOptions =
    subgroupsData?.subgrupo.filter((subgroup) => !subgroup.inativo) || [];

  useEffect(() => {
    if (!isNormalizingInitialUrl) {
      return;
    }

    if (location.search !== "?page=1") {
      navigate("/produtos?page=1", { replace: true });
      return;
    }

    setIsNormalizingInitialUrl(false);
  }, [isNormalizingInitialUrl, location.search, navigate]);

  const getProductsQueryString = (queryPage: number | null) => {
    const productFilterParams = new URLSearchParams({
      page: `${queryPage || 1}`,
    });

    if (filter) {
      productFilterParams.set("filtro", filter);
    }
    if (segmento) {
      productFilterParams.set("segmento", segmento);
    }
    if (grupo) {
      productFilterParams.set("grupo", grupo);
    }
    if (subgrupo) {
      productFilterParams.set("subgrupo", subgrupo);
    }

    return productFilterParams.toString().replace(/%2C/g, ",");
  };

  const searchProducts = () => {
    const productFilterParams = new URLSearchParams({ page: "1" });
    const trimmedSearchValue = searchValue.trim();
    const selectedSegmentIds = selectedSegments
      .map((segment) => segment.id)
      .join(",");
    const selectedGroupIds = selectedGroups.map((group) => group.id).join(",");
    const selectedSubgroupIds = selectedSubgroups
      .map((subgroup) => subgroup.id)
      .join(",");

    if (trimmedSearchValue) {
      productFilterParams.set("filtro", trimmedSearchValue);
    }
    if (selectedSegmentIds) {
      productFilterParams.set("segmento", selectedSegmentIds);
    }
    if (selectedGroupIds) {
      productFilterParams.set("grupo", selectedGroupIds);
    }
    if (selectedSubgroupIds) {
      productFilterParams.set("subgrupo", selectedSubgroupIds);
    }

    const productFilterQuery = productFilterParams
      .toString()
      .replace(/%2C/g, ",");

    navigate(`/produtos?${productFilterQuery}`);
  };

  const prepareModal = (id: string): void => {
    setModalId(id);
    setOpenModal(true);
  };

  const closeModal = (): void => {
    setOpenModal(false);
    setModalId("");
  };

  const renderState = () => {
    if (data && data.produtos.length > 0) {
      if (visualization === "CARD") {
        return data?.produtos.map((product) => {
          return (
            <>
              <Grid item xs={12} md={4} key={product.codigo}>
                <ProductCard
                  key={product.codigo}
                  produto={product}
                  toggle={prepareModal}
                />
              </Grid>
            </>
          );
        });
      } else {
        return (
          <Grid item xs={12} md={12}>
            <ProductsListingTable
              products={data ? data.produtos : []}
              toggle={prepareModal}
            />
          </Grid>
        );
      }
    } else {
      return <SearchFeedback />;
    }
  };

  if (isNormalizingInitialUrl || isLoading || isFetching) {
    return <Spinner />;
  }

  if (isError || error) {
    return <h2>Erro ao carregar Produtos</h2>;
  }

  return (
    <>
      <ProductDetailModal
        openModal={openModal}
        id={modalId}
        toggle={closeModal}
      />
      <Stack
        width={"100%"}
        mb={3}
        direction={"row"}
        justifyContent={"left"}
        sx={{ alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}
      >
        <TextField
          size="small"
          value={searchValue}
          placeholder={"Pesquisar"}
          type="search"
          onChange={(ev) => setSearchValue(ev.target.value)}
          sx={{ width: { xs: "100%", sm: "calc(50% - 8px)", md: "30%" } }}
          color="success"
          label="Pesquisar"
          onKeyPress={(ev) => {
            if (ev.key === "Enter") {
              searchProducts();
            }
          }}
        />
        <Autocomplete<ProductComboItem, true, false, false>
          size="small"
          multiple
          id="segmentos"
          options={segmentOptions}
          value={selectedSegments}
          onChange={(ev, value) => setSelectedSegments(value)}
          getOptionLabel={(option) => option.nome}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={productComboFilterOptions}
          filterSelectedOptions
          noOptionsText="Sem opções"
          loading={isSegmentsFetching || isSegmentsLoading}
          loadingText="Carregando..."
          renderOption={renderProductComboOption}
          sx={{ width: { xs: "100%", sm: "calc(50% - 8px)", md: "20%" } }}
          renderInput={(params) => (
            <TextField color="success" {...params} label="Segmento" />
          )}
        />
        <Autocomplete<ProductComboItem, true, false, false>
          size="small"
          multiple
          id="grupos"
          options={groupOptions}
          value={selectedGroups}
          onChange={(ev, value) => setSelectedGroups(value)}
          getOptionLabel={(option) => option.nome}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={productComboFilterOptions}
          filterSelectedOptions
          noOptionsText="Sem opções"
          loading={isGroupsFetching || isGroupsLoading}
          loadingText="Carregando..."
          renderOption={renderProductComboOption}
          sx={{ width: { xs: "100%", sm: "calc(50% - 8px)", md: "20%" } }}
          renderInput={(params) => (
            <TextField color="success" {...params} label="Grupo" />
          )}
        />
        <Autocomplete<ProductComboItem, true, false, false>
          size="small"
          multiple
          id="subgrupos"
          options={subgroupOptions}
          value={selectedSubgroups}
          onChange={(ev, value) => setSelectedSubgroups(value)}
          getOptionLabel={(option) => option.nome}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={productComboFilterOptions}
          filterSelectedOptions
          noOptionsText="Sem opções"
          loading={isSubgroupsFetching || isSubgroupsLoading}
          loadingText="Carregando..."
          renderOption={renderProductComboOption}
          sx={{ width: { xs: "100%", sm: "calc(50% - 8px)", md: "20%" } }}
          renderInput={(params) => (
            <TextField color="success" {...params} label="Subgrupo" />
          )}
        />
        <IconButton
          onClick={() => searchProducts()}
          sx={{
            backgroundColor: Colors.primary,
            "&:focus": {
              backgroundColor: Colors.primary,
            },
            "&:hover": {
              backgroundColor: "green",
            },
            boxShadow: 3,
            flexShrink: 0,
            height: 40,
            minWidth: 40,
            width: 40,
          }}
        >
          <Search sx={{ color: "white" }} />
        </IconButton>
      </Stack>
      <Grid container spacing={2} mb={visualization === "CARD" ? 2 : 0}>
        {renderState()}
      </Grid>
      {data && data?.totalPages > 1 && (
        <Pagination
          showFirstButton
          showLastButton
          page={page}
          count={data?.totalPages}
          sx={{
            "& .MuiPaginationItem-root": {
              "&.Mui-selected": {
                background: Colors.mainLayoutAtiveBackground,
                color: "black",
              },
            },
          }}
          renderItem={(item) => (
            <PaginationItem
              component={Link}
              to={`/produtos?${getProductsQueryString(item.page)}`}
              {...item}
            />
          )}
        />
      )}
    </>
  );
};

export default ProductsListPage;
